import React, { useEffect, useState } from "react";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

export type TextFieldPriceProps = Omit<TextFieldProps, "value"> & {
  /**
   * 内部で保持する生の値（カンマなしの文字列）
   * 例: "" | "1000"
   */
  value?: string;
  /**
   * 表示時に使うロケール（デフォルト "ja-JP"）
   */
  locale?: string;
  /**
   * 先頭に表示する通貨記号などを入れたい場合に使う（例: "¥"）
   * 既に InputProps.startAdornment を渡す場合は優先しないでください。
   */
  currencySymbol?: React.ReactNode;
};

// コンマを挿入する
const insertCommas = (raw: string, locale = "ja-JP"): string => {
  if (raw === "") return raw;
  // 小数は許可しないため小数点処理は不要
  const intPart = raw === "" ? "0" : raw;
  const intNumber = Number(intPart);
  const formattedInt =
    /^\d+$/.test(intPart) && Number.isFinite(intNumber)
      ? new Intl.NumberFormat(locale).format(intNumber)
      : intPart;

  return formattedInt;
};

// 数値のみに変換する
const sanitize = (input: string): string => {
  // カンマ・空白を除去
  let s = input.replace(/[,\s]/g, "");
  // 負の値は不要なので "-" はすべて除去する
  s = s.replace(/-/g, "");
  // 小数は許可しない（小数点をすべて削除）
  s = s.replace(/\./g, "");
  // 数字以外を除去
  s = s.replace(/[^0-9]/g, "");
  return s;
};

export const TextFieldPrice: React.FC<TextFieldPriceProps> = ({
  value = "",
  onChange,
  locale = "ja-JP",
  currencySymbol,
  InputProps,
  onFocus,
  onBlur,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const [display, setDisplay] = useState<string>("");

  // 外部 value が変わったら（かつフォーカス中でなければ）表示更新
  useEffect(() => {
    setDisplay(focused ? value ?? "" : insertCommas(value ?? "", locale));
  }, [value, focused, locale]);

  const handleFocus: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setFocused(true);
    setDisplay(value ?? "");
    onFocus?.(e);
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setFocused(false);
    setDisplay(insertCommas(value ?? "", locale));
    onBlur?.(e);
  };

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    const newE = { ...e };
    const rawInput = newE.target.value;
    const sanitized = sanitize(rawInput);
    newE.target.value = sanitized;
    onChange?.(newE);
    // フォーカス中は生の sanitized をそのまま表示（カンマなし）
    setDisplay(sanitized);
  };

  // マテリアルの InputProps に currencySymbol を優先的に追加（ユーザ指定優先）
  const mergedInputProps = {
    ...InputProps,
    startAdornment:
      InputProps?.startAdornment ??
      (currencySymbol ? (
        <InputAdornment position="start">{currencySymbol}</InputAdornment>
      ) : undefined),
  };

  return (
    <TextField
      {...rest}
      InputProps={mergedInputProps}
      value={display}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      inputProps={{
        inputMode: "numeric",
        pattern: "[0-9]*",
        ...rest.inputProps,
      }}
    />
  );
};

export default TextFieldPrice;
